import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { useCustomerAuth } from "@/contexts/CustomerAuthContext";
import { safeJsonArrayGet, safeSet } from "@/lib/storage";

const RequestsContext = createContext();

const readStored = () => {
  const storage = typeof window !== "undefined" ? window.localStorage : null;
  return safeJsonArrayGet(storage, "tw_requests", []).filter((item) => item && typeof item === "object");
};

export const RequestsProvider = ({ children }) => {
  const { customer } = useCustomerAuth();
  const [items, setItems] = useState(readStored);

  useEffect(() => {
    const storage = typeof window !== "undefined" ? window.localStorage : null;
    safeSet(storage, "tw_requests", JSON.stringify(Array.isArray(items) ? items : []));
  }, [items]);

  const remember = useCallback((record) => {
    if (!record?.number) return;
    setItems((prev) => {
      const current = Array.isArray(prev) ? prev : [];
      return [record, ...current.filter((item) => item?.number !== record.number)];
    });
  }, []);

  const refresh = useCallback(async () => {
    const stored = readStored();
    const refreshed = await Promise.all(stored.map(async (item) => {
      if (!item?.number || !item?.tracking_token) return item;
      try {
        const { data } = await api.get(`/requests/track/${encodeURIComponent(item.number)}`, {
          params: { token: item.tracking_token },
        });
        return { ...item, ...data, tracking_token: item.tracking_token };
      } catch {
        return item;
      }
    }));

    let accountItems = [];
    if (customer) {
      try {
        const { data } = await api.get("/customer-data/requests");
        accountItems = Array.isArray(data) ? data : [];
      } catch {
        accountItems = [];
      }
    }

    const merged = [...accountItems, ...refreshed].reduce((result, item) => {
      if (!item || typeof item !== "object") return result;
      const key = item.number || item.id;
      if (key && !result.some((current) => (current.number || current.id) === key)) result.push(item);
      return result;
    }, []);
    setItems(merged);
  }, [customer]);

  useEffect(() => {
    refresh().catch(() => {});
    const timer = window.setInterval(() => { refresh().catch(() => {}); }, 20000);
    const sync = () => { setItems(readStored()); window.setTimeout(() => { refresh().catch(() => {}); }, 50); };
    window.addEventListener("tw:requests-updated", sync);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("tw:requests-updated", sync);
    };
  }, [refresh]);

  const add = async (payload) => {
    const { data } = await api.post("/requests", {
      request_type: payload.sector === "services" ? "service_request" : `${payload.transaction || (payload.intent === "sell" ? "sell" : "buy")}_${payload.sector || "immobilier"}`,
      source: "request_wizard",
      name: payload.name,
      phone: payload.phone,
      whatsapp: payload.whatsapp || payload.phone,
      email: payload.email,
      service_id: payload.service_id || null,
      project: payload.project,
      country: payload.country || "CD",
      city: payload.city,
      zone: payload.zone,
      budget_min: payload.budget_min === "" ? null : Number(payload.budget_min),
      budget_max: payload.budget_max === "" ? null : Number(payload.budget_max),
      message: payload.criteria,
      details: { sector: payload.sector, transaction: payload.transaction, country: payload.country || "CD", ...(payload.details || {}) },
    });
    const rec = { ...payload, ...data, status: data.status || "nouveau" };
    remember(rec);
    return rec;
  };

  const remove = (number) => setItems((prev) => (Array.isArray(prev) ? prev : []).filter((item) => item?.number !== number));

  return (
    <RequestsContext.Provider value={{ items: Array.isArray(items) ? items : [], add, remember, refresh, remove }}>
      {children}
    </RequestsContext.Provider>
  );
};

export const useRequests = () => useContext(RequestsContext);
